import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import * as d3 from 'd3';

import {Observable, Subject} from 'rxjs';
import {isDefined} from '../../commons/commons';
import {takeUntil} from 'rxjs/operators';
import {bisectCenter, pointer} from 'd3/dist/d3'; // workaround due to @types/d3 being old w.r.t. D3
import * as _ from 'lodash';

const DEFAULT_MARGIN = 20;
const DEFAULT_COLOR = 'black';
const DEFAULT_SPACE_BETWEEN_X_TICKS = 80;
const DEFAULT_SPACE_BETWEEN_Y_TICKS = 40;
const DEFAULT_PADDING = 0.1;
const DEFAULT_STROKE_WIDTH = 1.5;
const DEFAULT_X_AXIS = true;
const DEFAULT_Y_AXIS = false;
const DEFAULT_TOOLTIP_BORDER_COLOR = '#EEEEEE';
const DEFAULT_TOOLTIP_BACKGROUND_COLOR = 'white';
const DEFAULT_SELECTION_LINE_COLOR = '#BDBDBD';
const DEFAULT_BAR_OPACITY = 0.75;
const DEFAULT_TIME_FORMAT = '%d/%m %H:%M';

export interface ChartMargins {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
}

export interface SharedEnergyData { // all data meant to be positive
    time: number;
    absorbedEnergy: number; // >=  0
    injectedEnergy: number; // >=  0
    sharedEnergy: number; // >=  0
    opacity: number;
    accountedAbsorbedEnergy?: number; // >=  0
    accountedInjectedEnergy?: number; // >=  0
    accountedSharedEnergy?: number; // >=  0
    accountedOpacity?: number;
    strokeWidth?: number;
    accountedStrokeWidth?: number;
}

export interface TextNode {
    text: string;
    color?: string;
    bold?: boolean;
    italic?: boolean;
}

interface MouseSelection {
    currPoint: [number, number];
    selectedPoint: [number, number];
    selectedData: SharedEnergyData;
}

@Component({
    selector: 'app-shared-energy-chart',
    templateUrl: './shared-energy-chart.component.html',
    styleUrls: ['./shared-energy-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SharedEnergyChartComponent implements OnInit, OnChanges, OnDestroy {

    private destroyed = new Subject<void>();

    @Input() data: SharedEnergyData[];
    @Input() absorptionColor: string;
    @Input() injectionColor: string;
    @Input() sharedColor: string;
    @Input() consolidatedInjectionColor: string;
    @Input() consolidatedAbsorptionColor: string;
    @Input() consolidatedSharedColor: string;
    @Input() margins: ChartMargins;
    @Input() xTickSpace: number;
    @Input() yTickSpace: number;
    @Input() yTickFormatter: (value: number) => string;
    @Input() padding: number;
    @Input() xGrid: boolean;
    @Input() xAxis: boolean;
    @Input() yAxis: boolean;
    @Input() yGrid: boolean;
    @Input() gridColor: string;
    @Input() tooltipFormatter: (data: SharedEnergyData) => TextNode[][]; // external array means lines of text
    @Input() tooltipBorderColor: string;
    @Input() tooltipBackgroundColor: string;
    @Input() selectionLineColor: string;
    @Input() resizeTrigger: Observable<void>;

    @ViewChild('chart', {static: false}) private chartContainer: ElementRef;

    constructor() {
    }

    ngOnInit() {
        setTimeout(() => {
            if (this.data) {
                this.destroyChart();
                this.createChart();
            }
        }, 0); // delayed, so that CSS styles (see "width: 100%") will have been computed
        if (this.resizeTrigger) {
            // re-create chart upon external resizing (may not be caught by window's resizing host listener)
            this.resizeTrigger.pipe(
                takeUntil(this.destroyed)
            ).subscribe(() => {
                this.destroyChart();
                this.createChart();
            });
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        // if not first change, rebuild chart
        if (Object.getOwnPropertyNames(changes).find(p => !changes[p].isFirstChange())) {
            this.destroyChart();
            this.createChart();
        }
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    // noinspection JSUnusedLocalSymbols (see https://stackoverflow.com/questions/50405930/aot-angular-6-directive-somecomponent-expected-0-arguments-but-got-1-for)
    @HostListener('window:resize', ['$event'])
    onResize(event) {
        // destroy and rebuild chart upon resizing
        this.destroyChart();
        this.createChart();
    }

    private createChart() {
        const element = this.chartContainer.nativeElement;

        const margins = {
            top: this.margins && isDefined(this.margins.top) ? this.margins.top : DEFAULT_MARGIN,
            right: this.margins && isDefined(this.margins.right) ? this.margins.right : DEFAULT_MARGIN,
            bottom: this.margins && isDefined(this.margins.bottom) ? this.margins.bottom : DEFAULT_MARGIN,
            left: this.margins && isDefined(this.margins.left) ? this.margins.left : DEFAULT_MARGIN
        };

        const width = element.offsetWidth - margins.left - margins.right;
        const height = element.offsetHeight - margins.top - margins.bottom;

        // quick fix for "transients"
        if (width - margins.left - margins.right < 0 || height - margins.top - margins.bottom < 0) {
            return;
        }

        // basic SVG container
        const svg = d3.select(element)
            .append('svg')
            .attr('width', element.offsetWidth)
            .attr('height', element.offsetHeight);

        // x axis
        const showXAxis = isDefined(this.xAxis) ? this.xAxis : DEFAULT_X_AXIS;
        const xTickSpace = isDefined(this.xTickSpace) ? this.xTickSpace : DEFAULT_SPACE_BETWEEN_X_TICKS;
        const xScale = d3.scaleBand<number>()
            // @ts-ignore
            .domain(d3.range(this.data.length))
            .range([margins.left, width - margins.right])
            .padding(isDefined(this.padding) ? this.padding : DEFAULT_PADDING);
        const xTickStep = Math.ceil(xScale.domain().length / width * xTickSpace);
        const xAxis = g => g
            .attr('transform', `translate(0,${height - margins.bottom})`)
            .call(d3.axisBottom(xScale)
                // @ts-ignore
                .tickFormat(i => d3.timeFormat(DEFAULT_TIME_FORMAT)(this.data[i].time))
                .tickSizeOuter(0)
                .tickValues(xScale.domain().filter((d, i) => (i % xTickStep) === 0)))
            .call(g => !showXAxis ? g.select('.domain').remove() : g)
            .selectAll('text')
            .style('text-anchor', 'end')
            // .attr('dx', '-.8em')
            // .attr('dy', '.15em')
            .attr('transform', 'rotate(-65)');

        // value axis
        const showYAxis = isDefined(this.yAxis) ? this.yAxis : DEFAULT_Y_AXIS;
        const yTickSpace = isDefined(this.yTickSpace) ? this.yTickSpace : DEFAULT_SPACE_BETWEEN_Y_TICKS;
        const yScale = d3.scaleLinear()
            .domain([
                d3.min(this.data, d => d3.min([-d.absorbedEnergy, -d.accountedAbsorbedEnergy])),
                d3.max(this.data, d => d3.max([d.injectedEnergy, d.sharedEnergy, d.accountedInjectedEnergy, d.accountedSharedEnergy]))
            ])
            .nice()
            .range([height - margins.bottom, margins.top]);
        const yAxis = g => g
            .attr('transform', `translate(${margins.left},0)`)
            .call(d3.axisLeft(yScale)
                .tickFormat(this.yTickFormatter || (y => `${y}`))
                .tickSizeOuter(0)
                .ticks(Math.floor(height / yTickSpace)))
            .call(g => !showYAxis ? g.select('.domain').remove() : g);

        // shared energy line
        const line = d3.line<SharedEnergyData>()
            .defined(d => isDefined(d.sharedEnergy) && !isNaN(d.sharedEnergy))
            .x((d, i) => xScale(i) + xScale.bandwidth() / 2)
            .y(d => yScale(d.sharedEnergy ));

        // shared energy line
        const lineAccounted = d3.line<SharedEnergyData>()
            .defined(d => isDefined(d.accountedSharedEnergy) && !isNaN(d.accountedSharedEnergy))
            .x((d, i) => xScale(i) + xScale.bandwidth() / 2)
            .y(d => yScale(d.accountedSharedEnergy ));

        // populate chart with (optional) grids, axes and bars
        svg.append('g')
            .call(xAxis);
        svg.append('g')
            .call(yAxis);
        if (!!this.xGrid) {
            const xAxisGrid = d3.axisBottom(xScale)
                .tickSize(-(height - margins.bottom - margins.top))
                .tickFormat(() => '')
                .ticks(width / xScale.bandwidth());
            svg.append('g')
                .attr('class', 'x axis')
                .attr('transform', 'translate(0,' + (height - margins.bottom) + ')')
                .style('stroke-dasharray', ('3,3'))
                .call(xAxisGrid)
                .call(g => g.select('.domain').remove())
                .call(g => g.selectAll('.tick line')
                    .style('stroke', this.gridColor || DEFAULT_COLOR));
        }
        if (!!this.yGrid) {
            const yAxisGrid = d3.axisLeft(yScale)
                .tickSize(-(width - margins.right - margins.left))
                .tickFormat(() => '')
                .ticks(height / yTickSpace);
            svg.append('g')
                .attr('class', 'y axis')
                .attr('transform', 'translate(' + margins.left + ',0)')
                .style('stroke-dasharray', ('3,3'))
                .call(yAxisGrid)
                .call(g => g.select('.domain').remove())
                .call(g => g.selectAll('.tick line')
                    .style('stroke', this.gridColor || DEFAULT_COLOR))
                .call(g => g.selectAll('.tick:first-of-type line').remove());
        }
        // Function to draw a histogram
        function drawHistogram(svg: d3.Selection<SVGSVGElement, unknown, null, undefined>, data: SharedEnergyData[], valueKey: string, color: string, opacityKey: string, defaultOpacity: number, negative: boolean) {
            svg.append('g')
                .attr('fill', color || DEFAULT_COLOR)
                .selectAll('rect')
                .data(data)
                .join('rect')
                .attr('fill-opacity', d => isDefined(d[opacityKey]) ? d[opacityKey] : defaultOpacity)
                .attr('x', (d, i) => xScale(i))
                .attr('y', d => negative ? yScale(0) : yScale(d[valueKey] || 0))
                .attr('height', d => negative ? yScale(-(d[valueKey] || 0)) - yScale(0) :
                    yScale(0) - yScale(d[valueKey] || 0))
                .attr('width', xScale.bandwidth());
        }

        // Function to draw a line
        function drawLine(svg: d3.Selection<SVGSVGElement, unknown, null, undefined>, data: SharedEnergyData[], valueKey: string, strokeKey: string, color: string, dashArray: string, line: d3.Line<SharedEnergyData>) {
            svg.append('path')
                .datum(data)
                .attr('fill', 'none')
                .attr('stroke', color || DEFAULT_COLOR)
                .attr('stroke-width', d => isDefined(d.find(sd => !!sd[strokeKey])[strokeKey]) ? d.find(sd => !!sd[strokeKey])[strokeKey] : DEFAULT_STROKE_WIDTH)
                .attr('stroke-linejoin', 'round')
                .attr('stroke-linecap', 'round')
                .attr('stroke-dasharray', dashArray)
                .attr('d', line);
        }
        // draw injected energy histogram
        if (this.data.some(d => !!d.injectedEnergy)) {
            drawHistogram(svg, this.data, 'injectedEnergy', this.injectionColor, 'opacity', DEFAULT_BAR_OPACITY, false);
        }
        // draw histogram absorbed energy (accounted)
        if (this.data.some(d => !!d.accountedInjectedEnergy)) {
            drawHistogram(svg, this.data, 'accountedInjectedEnergy', this.consolidatedInjectionColor, 'accountedOpacity', DEFAULT_BAR_OPACITY, false);
        }
        // draw absorbed energy histogram
        if (this.data.some(d => !!d.absorbedEnergy)) {
            drawHistogram(svg, this.data, 'absorbedEnergy', this.absorptionColor, 'opacity', DEFAULT_BAR_OPACITY, true);
        }
        // draw histogram absorbed energy (accounted)
        if (this.data.some(d => !!d.accountedAbsorbedEnergy)) {
            drawHistogram(svg, this.data, 'accountedAbsorbedEnergy', this.consolidatedAbsorptionColor, 'accountedOpacity', DEFAULT_BAR_OPACITY, true);
        }
        // draw shared energy line (statistical)
        if (this.data.some(d => !!d.sharedEnergy)) {
            drawLine(svg, this.data, 'sharedEnergy', 'strokeWidth',  this.sharedColor, calculateDashArray(this.data.length), line);
        }
        // draw shared energy line (accounted)
        if (this.data.some(d => !!d.accountedSharedEnergy)) {
            drawLine(svg, this.data, 'accountedSharedEnergy', 'accountedStrokeWidth', this.consolidatedSharedColor, null, lineAccounted);
        }

        // tooltip-related stuff
        const focus = svg.append('g')
            .style('display', null)
            .style('pointer-events', 'none');
        const selectionPoint = focus.append('circle');
        const selectionLine = focus.append('line');
        const tooltip = focus.append('g');

        const ttCallout = (g: d3.Selection<SVGElement, unknown, null, undefined>, ttText: TextNode[][], sel: MouseSelection) => {
            if (!ttText || !sel) {
                focus.style('display', 'none');
                return;
            }
            focus.style('display', null);

            selectionPoint.style('display', null)
                .attr('r', 3)
                .attr('cx', sel.selectedPoint[0])
                .attr('cy', sel.selectedPoint[1])
                .attr('fill', this.tooltipBackgroundColor || DEFAULT_TOOLTIP_BACKGROUND_COLOR)
                .attr('stroke', this.sharedColor || DEFAULT_COLOR)
                .attr('stroke-width', 1.5);

            // NOTE: we could, in principle, draw a circle around nearest point as well
            // NOTE: we could, in principle, draw a circle around nearest point as well
            selectionLine.attr('x1', sel.currPoint[0])
                .attr('y1', height - margins.bottom)
                .attr('x2', sel.currPoint[0])
                .attr('y2', margins.top)
                .style('stroke-width', 1)
                .style('stroke', this.selectionLineColor || DEFAULT_SELECTION_LINE_COLOR)
                .style('fill', 'none');

            const path = g.selectAll('path')
                .data([null])
                .join('path')
                .attr('fill', this.tooltipBackgroundColor || DEFAULT_TOOLTIP_BACKGROUND_COLOR)
                .attr('stroke', this.tooltipBorderColor || DEFAULT_TOOLTIP_BORDER_COLOR);

            const lines = _.flatten(ttText.map((line, idx) => line.map(entry => ({...entry, lineIdx: idx}))));

            const text = g.selectAll('text')
                .data([null])
                .join('text')
                .call(txt => txt
                    .selectAll('tspan')
                    .data(lines)
                    .join('tspan')
                    .attr('x', (d, i) => (i === 0 || lines[i].lineIdx !== lines[i - 1].lineIdx) ? 0 : null)
                    .attr('y', (d, i) => `${d.lineIdx * 1.5}em`)
                    .attr('fill', d => d.color || DEFAULT_COLOR)
                    .style('font-weight', d => d.bold ? 'bold' : null)
                    .style('font-style', d => d.italic ? 'italic' : null)
                    .text(d => d.text));

            const {y, width: w, height: h} = (text.node() as SVGGraphicsElement).getBBox();

            let xOffset;
            if (sel.currPoint[0] + w > width - margins.right) {
                text.attr('transform', `translate(${-w},${15 - y})`);
                path.attr('d', `M${-w - 10},5H-5l5,0l5,0H${10}v${h + 20}h-${w + 20}z`);
                xOffset = -20;
            } else {
                text.attr('transform', `translate(${0},${15 - y})`);
                path.attr('d', `M${-10},5H-5l5,0l5,0H${w + 10}v${h + 20}h-${w + 20}z`);
                xOffset = +20;
            }

            // set tooltip's position
            g.attr('transform', `translate(${sel.currPoint[0] + xOffset},${margins.top + 20})`)
                .style('pointer-events', 'none')
                .style('font', '10px sans-serif');
        };

        const computeMouseSelection = ([mx, my]: [number, number]): MouseSelection => {
            const domain = xScale.domain();
            const halfBandwidth = xScale.bandwidth() / 2;
            const step = xScale.step();
            const firstPointX = xScale(domain[0]) + halfBandwidth;
            const lastPointX = xScale(domain[domain.length - 1]) + halfBandwidth;
            const [yMax, yMin] = yScale.range();
            if (mx < firstPointX || mx > lastPointX || my < yMin || my > yMax) {
                return null;
            }
            const index = bisectCenter(domain.map((d, i) => firstPointX + i * step), mx);
            if (!isDefined(index)) {
                return null;
            }
            const selData = this.data[index];
            return {
                currPoint: [mx, my],
                selectedPoint: [xScale(domain[index]) + halfBandwidth, yScale(selData.sharedEnergy)],
                selectedData: isDefined(selData.sharedEnergy) ? selData : null
            };
        };

        const component = this;

        function tooltipCb(event) {
            const ms = computeMouseSelection(pointer(event, this));
            if (!ms) { // clear tooltip in case of pointer-outside or "no data"
                tooltip.call(ttCallout, null, null);
                return;
            }
            tooltip.call(ttCallout, component.tooltipFormatter(ms.selectedData), ms);
        }

        if (this.tooltipFormatter) {
            svg.on('touchmove mousemove', tooltipCb);
            svg.on('touchend mouseleave', () => {
                tooltip.call(ttCallout, null, null);
            });
        }

        function calculateDashArray(dataLength: number): string {
            const dashLength = 4;
            const n = dataLength * dashLength;
            const dashArray = Array.from({ length: n / dashLength }, () => dashLength).join(', ');
            return `0, ${dashArray}`;
        }
    }

    private destroyChart() {
        d3.select(this.chartContainer.nativeElement)
            .select('svg')
            .remove();
    }
}
